import React, { ChangeEvent, FC, useState } from "react";
import useWindowDimensions from "../../../hooks/useWindowDimensions";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import CustomTextInput from "../../../shared/CustomInput/CustomTextInput";
import Typhography from "../../../shared/Typhography/Typhography";
import "./OnBoardingLogin.scss";

interface Step<T extends string = string> {
  type: "string" | "number" | "radio" | "select";
  placeholder?: string;
  disableIf?: (form: any) => boolean;
  answer: string;
  rightAddon?: string;
  options?: { label: string; value: any };
  key: T;
}

interface IOnBoardingLogin {
  steps: Step[];
  onConfirm?: (form: Record<string, any>) => void;
}

const OnBoardingLogin: FC<IOnBoardingLogin> = ({ steps, onConfirm }) => {
  const { getClassesNameByQuery } = useWindowDimensions();
  const [form, setForm] = useState<Record<string, any>>({});

  const [isConfirmed, setIsConfirmed] = useState(true);
  const [historyIndexSteps, setHistoryIndexSteps] = useState([0]);

  const setKeyForm = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const renderAcceptAndConfirmStep = () => {
    return (
      <>
        <h1
          className={`${getClassesNameByQuery({
            xs: {
              "text-bold-20": true,
            },
            sm: {
              "text-bold-24": true,
            },
          })}`}
        >
          Prima di cominciare abbiamo qualche domanda per te..
        </h1>
        <span
          className={`${getClassesNameByQuery({
            xs: {
              "text-regular-16": true,
            },
            sm: {
              "text-regular-16": true,
            },
          })}`}
        >
          Per migliorare la tua esperienza con Leopart e offrirti un servizio
          adatto a te di preghiamo di rispondere a questo questionario. I dati
          saranno gestiti secondo le norme in materia, per più informazioni
          consulta la nostra <b>privacy policy</b>.
        </span>
        <div className="d-flex justify-content-center pt-3">
          <CustomButton
            onClick={() => {
              setIsConfirmed(true);
            }}
          >
            Accetta e Conferma
          </CustomButton>
        </div>
      </>
    );
  };

  const isLastStep = () => {
    const lastIndex = historyIndexSteps[historyIndexSteps.length - 1];
    const indexNextStep = steps.findIndex(
      (nextStep, index) => index > lastIndex && !nextStep.disableIf?.(form)
    );

    return indexNextStep === -1;
  };

  const sanitizeForm = () => {
    const invalidKey = steps
      .filter(({ disableIf }) => disableIf?.(form))
      .map(({ key }) => key);

    return Object.fromEntries(
      Object.entries(form).filter(([key]) => !invalidKey.includes(key))
    );
  };

  const handleNextStep = () => {
    const lastIndex = historyIndexSteps[historyIndexSteps.length - 1];
    const indexNextStep = steps.findIndex(
      (nextStep, index) => index > lastIndex && !nextStep.disableIf?.(form)
    );
    if (indexNextStep === -1) {
      onConfirm?.(sanitizeForm());
      return;
    }

    setHistoryIndexSteps((prev) => {
      if (indexNextStep < 0) return prev;
      return [...prev, indexNextStep];
    });
  };

  const handleBackStep = () => {
    setHistoryIndexSteps((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, historyIndexSteps.length - 1);
    });
  };

  const renderStep = () => {
    const { type, key, placeholder, answer, rightAddon } =
      steps[historyIndexSteps[historyIndexSteps.length - 1]];
    let input;
    switch (type) {
      case "string":
        input = (
          <CustomTextInput
            placeholder={placeholder}
            rightAddon={rightAddon}
            value={form?.[key] ?? ""}
            onChange={({
              target: { value },
            }: ChangeEvent<HTMLInputElement>) => {
              setKeyForm(key, value);
            }}
          />
        );
        break;

      case "number":
        input = (
          <CustomTextInput
            type="number"
            placeholder={placeholder}
            value={form?.[key] ?? ""}
            rightAddon={rightAddon}
            onChange={(_, value) => {
              setKeyForm(key, value);
            }}
          />
        );
        break;

      default:
        break;
    }

    return (
      <div className="container__step">
        <Typhography type="h4" size="bold-20">
          {answer}
        </Typhography>
        {input}
        <CustomButton fullWidth onClick={handleBackStep}>
          Indietro
        </CustomButton>
        <CustomButton fullWidth onClick={() => handleNextStep()}>
          {isLastStep() ? "Conferma" : "Avanti"}
        </CustomButton>
      </div>
    );
  };

  return (
    <div className="container__on__boarding__login">
      {isConfirmed ? renderStep() : renderAcceptAndConfirmStep()}
    </div>
  );
};

export default OnBoardingLogin;
